import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn, formatNumber } from '@/utils';
import { SessionStore, SessionType, useSessionStore } from '@/hooks/useSessionStore';
import { Dialog } from '@/components/Dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChevRightFilledIcon, CloseFilledIcon, EditFilledIcon, GuitarPixelIcon, LogoutFilledIcon, MenuIcon } from '@/components/Icons';
import { updateUserName } from '@/service/user';
import { useDocumentVisibility } from 'ahooks';
import { useRefreshData } from '@/hooks/useRefreshData';
import { useSubscriptionStore } from '@/state/subsription-store';
import dayjs from 'dayjs';
import { SubscriptPlanItem } from '@/service/type/subscription';
import { useLinksDetailShow } from '@/state/show-store';
import useFlags from '@jenmusic/feature-flag';

export const Avatar = ({ className, onClick }: { className?: string; onClick?: React.MouseEventHandler<HTMLDivElement> }) => {
  return <div className={cn('w-10 h-10 rounded-full bg-jen-grey-1', className)} onClick={(e) => onClick?.(e)}></div>;
};

export const Profile = () => {
  const sessionStore = useSessionStore();
  const { refreshSubscription } = useRefreshData();

  const documentVisibility = useDocumentVisibility();

  useEffect(() => {
    if (documentVisibility === 'visible') {
      refreshSubscription();
    }
  }, [documentVisibility]);

  return <ProfileUI sessionStore={sessionStore} />;
};

interface ProfileUIProps {
  sessionStore: SessionStore;
}

export const ProfileUI: React.FC<ProfileUIProps> = ({ sessionStore }: ProfileUIProps) => {
  const { session } = sessionStore;
  const { isShow: isLinksDetailShow, setIsShow: setIsLinksDetailShow } = useLinksDetailShow();
  return (
    <div className={cn('bg-jen-border grid gap-[1px] grid-cols-[1fr_72px]')}>
      <ProfileUserInfo session={session} />
      <div
        className="bg-jen-background-primary flex justify-center items-center cursor-pointer"
        onClick={() => {
          setIsLinksDetailShow(!isLinksDetailShow);
        }}
      >
        {isLinksDetailShow ? (
          <CloseFilledIcon fillColor="#67666d" className="text-jen-grey-3 " />
        ) : (
          <MenuIcon fillColor="#67666d" className="text-jen-grey-3 " />
        )}
      </div>
    </div>
  );
};

export const ProfileUserInfo: React.FC<{ session: SessionType }> = ({ session }) => {
  return (
    <div
      className=" px-[16px] py-[12px] flex flex-col justify-between bg-jen-background-primary cursor-pointer"
      onClick={() => {
        Dialog.info({
          dialogContentClassName: 'bg-[#EFEFF0] w-[568px]  lg:max-w-[568px]',
          contentJSX: ({ onChange }) => {
            return <ProfileModalContent />;
          },
        });
      }}
    >
      {/* <Avatar className="mr-[10px]" /> */}
      <div className="flex items-center ">
        <div>
          <Tag name={session.user?.plan?.name} />
        </div>
        <div className="ml-jen-spacing-4 text-jen-size-interactive-small text-jen-content-secondary !leading-[17px]">
          <span className="mr-jen-spacing-8">{formatNumber(session?.user?.credit?.value)}</span>
          CREDIT{session?.user?.credit?.value > 0 && 'S'} LEFT
        </div>
      </div>
      <div className="flex items-center ">
        {session?.user?.name}
        {session?.user?.name && <ChevRightFilledIcon className=" relative top-[-1px]" />}
      </div>
    </div>
  );
};

export const ProfileModalContent = () => {
  const { session, logout, fetchData } = useSessionStore();
  const { subscriptionStore } = useSubscriptionStore();

  return (
    <div className="mt-jen-spacing-48 text-jen-content-primary p-jen-spacing-16">
      <div className="text-jen-size-heading-06 text-jen-content-tertiary">ACCOUNT</div>
      <Tabs defaultValue="General" className="w-full">
        <TabsList className="bg-transparent text-jen-size-heading-02 mt-jen-spacing-12">
          <TabsTrigger value="General">General</TabsTrigger>
          <TabsTrigger value="Billing">Billing</TabsTrigger>
        </TabsList>
        <TabsContent className="pt-1 lg:pt-jen-spacing-24 " value="General">
          <Label> DISPLAY NAME</Label>
          <FieldEditor
            initValue={session.user.name}
            onChangeValue={async (val) => {
              //todo: involve name change api
              await updateUserName({ name: val });
              fetchData();
            }}
          />

          <Label className="mt-jen-spacing-24"> ACTIVE PLAN</Label>

          <ActivePlan subscriptionStore={subscriptionStore} session={session} />
          <div className="footer mt-[20px] lg:mt-[100px] flex justify-between">
            <div
              onClick={async () => {
                window.location.replace('/logout');
              }}
              className="text-[#67666d] cursor-pointer text-[16px]  flex px-jen-spacing-16 py-jen-spacing-12 rounded-jen-spacing-8 border border-[#67666d]"
            >
              <LogoutFilledIcon fillColor="currentColor" className="mr-jen-spacing-8" />
              Log Out
            </div>
            {/* <div className="text-jen-negative-sentiment cursor-pointer text-[16px] flex px-jen-spacing-16 py-jen-spacing-12 rounded-jen-spacing-8 border border-jen-negative-sentiment">
              Delete Account
            </div> */}
          </div>
        </TabsContent>
        <TabsContent className="pt-1 lg:pt-jen-spacing-24 " value="Billing">
          <Label>AMOUNT</Label>
          <div className="flex justify-between mt-jen-spacing-8 h-[26px]">
            <div className="text-jen-size-heading-04">
              {subscriptionStore?.name}: USD ${(subscriptionStore?.price || 0) / 100}
            </div>
          </div>
          <Label className="mt-jen-spacing-24">FREQUENCY</Label>

          <div className="flex justify-between mt-jen-spacing-8 h-[20px]">
            <div className="text-jen-size-heading-04">
              {subscriptionStore?.period_end
                ? `Monthly (Renews ${dayjs(subscriptionStore?.created_at).format('MMM DD, YYYY')})`
                : 'No Subscription'}
            </div>
          </div>
          <Label className="mt-jen-spacing-24">ACTIVE PLAN</Label>

          <ActivePlan subscriptionStore={subscriptionStore} session={session} />
          <div className="footer mt-[20px] lg:mt-[100px] flex justify-between">
            {subscriptionStore.price !== 0 && (
              <form action={`/api/v1/plan/subscription/base`} method="post">
                <button
                  type="submit"
                  className="text-jen-negative-sentiment cursor-pointer text-[16px] flex px-jen-spacing-16 py-jen-spacing-12 rounded-jen-spacing-8 border border-jen-negative-sentiment"
                >
                  Cancel Subscription
                </button>
              </form>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

interface FieldEditorProps {
  onChangeValue: (val: string) => void;
  initValue: string;
  showEdit?: boolean;
}

const FieldEditor: React.FC<FieldEditorProps> = ({ onChangeValue, initValue, showEdit = true }) => {
  const [showInput, setShowInput] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  return (
    <div className="flex justify-between mt-jen-spacing-8">
      {showInput ? (
        <input
          ref={inputRef}
          className="flex-1 mr-4 border-b border-jen-negative-sentiment h-[26px] text-[20px] appearance-none  focus:outline-none focus:ring-0 bg-transparent"
          defaultValue={initValue}
        />
      ) : (
        <div className="text-jen-size-heading-04">{initValue}</div>
      )}
      {showEdit && (
        <EditButton
          name={showInput ? 'Done' : 'Edit'}
          onClick={() => {
            if (showInput) {
              const value = inputRef?.current?.value;

              if (value?.trim()) {
                onChangeValue?.(value);
              }

              setShowInput(!showInput);
            } else {
              setShowInput(!showInput);
              setTimeout(() => {
                if (inputRef?.current) {
                  inputRef?.current?.focus();
                }
              });
            }
          }}
        />
      )}
    </div>
  );
};

const Label: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => {
  return <div className={cn('mt-jen-spacing-24 text-jen-size-heading-06 text-jen-content-tertiary', className)}>{children}</div>;
};

const ActivePlan: React.FC<{
  subscriptionStore: Partial<SubscriptPlanItem>;
  session: SessionType;
}> = ({ subscriptionStore, session }) => {
  return (
    <div className="text-[10px] lg:text-[14px] mt-jen-spacing-12 border-jen-border  border  rounded-jen-spacing-12 bg-jen-background-primary">
      <div className="  p-jen-spacing-16 flex justify-between items-center ">
        <GuitarPixelIcon className="flex-shrink-0 mr-1 lg:mr-jen-spacing-24 w-[32px] lg:w-[48px] h-[32px] lg:h-[48px]" />
        <div className="flex-1 ">
          <div className="text-jen-size-heading-04">{subscriptionStore?.name}</div>
          <div className="mt-0 lg:mt-jen-spacing-8 text-jen-size-heading-05 text-jen-content-secondary">
            USD ${(subscriptionStore?.price || 0) / 100}/month
          </div>
        </div>
        <div
          className=" cursor-pointer text-[#67666d] border rounded-[4px] lg:rounded-jen-spacing-8 p-1 lg:px-jen-spacing-16 lg:py-jen-spacing-12 mr-jen-spacing-8"
          onClick={() => {
            window.open('/recharge', '_blank');
          }}
        >
          Change Plans
        </div>
        <div
          onClick={() => {
            window.open('/recharge?tab=business', '_blank');
          }}
          className=" cursor-pointer text-jen-content-primary-contrast bg-jen-interactive-primary  rounded-[4px] lg:rounded-jen-spacing-8  p-1 lg:px-jen-spacing-16 lg:py-jen-spacing-12"
        >
          Upgrade Plans
        </div>
      </div>
      <div className="w-[calc(100%-32px)] m-auto border-t border-jen-border"></div>
      <div className=" p-jen-spacing-16 flex justify-between ">
        {
          <div className="text-jen-content-secondary text-[10px] lg:text-jen-size-heading-04 flex items-center gap-[8px] ">
            Monthly Credits Remaining
          </div>
        }

        <div className="flex items-center gap-[8px]">
          <div className="text-[#000] text-[10px] lg:text-jen-size-heading-04">
            {formatNumber(session?.user?.credit?.monthly_credit)} CRD
          </div>
          <div className=" cursor-pointer relative top-[-1px] underline font-pressura-mono text-[10px] lg:text-[14px] text-jen-interactive-secondary">
            <form action={`/api/v1/payment/credit_topup`} method="post">
              <button type="submit" className=" underline">
                PURCHASE CREDITS
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditButton = ({ onClick, name }: { onClick: () => void; name: string }) => {
  return (
    <div
      className="text-jen-size-interactive-small flex gap-[2px] leading-[16px] cursor-pointer items-center text-[14px] text-jen-content-tertiary p-jen-spacing-4 pr-jen-spacing-8 rounded-jen-spacing-4 border"
      onClick={onClick}
    >
      <EditFilledIcon className="w-[16px] h-[16px]" fillColor="currentColor" />
      {name}
    </div>
  );
};

interface TagProps {
  name?: string;
}
const Tag: React.FC<TagProps> = ({ name }) => {
  const classNameStr = useMemo(() => {
    switch (name) {
      case 'Novice':
        return 'bg-[#d4d5d5] text-[#4d4f51]';
      case 'Dabbler':
        return 'bg-[#9747ff] text-jen-content-primary-contrast';
      case 'Soloist':
        return 'bg-[#9747ff] text-jen-content-primary-contrast';
      case 'Pro':
        return 'bg-[#9747ff] text-jen-content-primary-contrast';
      default:
        return 'bg-[#d4d5d5] text-[#4d4f51]';
    }
  }, [name]);

  return (
    <div className={cn('text-[12px] rounded-[2px] leading-[12px] px-[16px] py-[4px] mr-[2px] bg-[#9747ff]', classNameStr)}>{name}</div>
  );
};
